// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';

// ** Components
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';
import ComponentSharedTextEditor from '@/components/strapi/Shared/ComponentSharedTextEditor';
import { getRankingLabel } from '@/features/ap-plus/mappers/mapCompetitionGrid';

// ** Types
import type { ImageEntry, CompetitionCardModel } from '@/components/common/CompetitionCard';

// ** Local
import MetaRow, { BODY_SM_DETAIL_SX } from './MetaRow';
import { getYouTubeId, getYouTubeThumbnail, isVideoEntry } from './videoUtils';

type CompetitionDetailDesktopProps = {
	item: CompetitionCardModel;
	allImages: ImageEntry[];
	selectedImageIndex: number;
	onSelectImage: (index: number) => void;
};

const nullToUndefined = <T,>(value: T | null | undefined): T | undefined => (value === null ? undefined : value);

const CompetitionDetailDesktop = ({
	item,
	allImages,
	selectedImageIndex,
	onSelectImage
}: CompetitionDetailDesktopProps) => {
	const current = allImages[selectedImageIndex] ?? allImages[0];
	const rankingLabel = getRankingLabel(item);

	const thumbsRef = React.useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = React.useState(false);
	const [canScrollRight, setCanScrollRight] = React.useState(false);

	const block = item?.detail?.fullDescription;

	const parsedContent = React.useMemo(() => {
		if (typeof block?.blockContent !== 'string') return null;

		try {
			return JSON.parse(block.blockContent);
		} catch (error) {
			console.error('Failed to parse Strapi blockContent JSON:', error);
			return null;
		}
	}, [block?.blockContent]);

	const updateScrollIndicators = React.useCallback(() => {
		const el = thumbsRef.current;
		if (!el) return;
		setCanScrollLeft(el.scrollLeft > 1);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
	}, []);

	React.useEffect(() => {
		updateScrollIndicators();
		const el = thumbsRef.current;
		if (!el) return;
		const observer = new ResizeObserver(updateScrollIndicators);
		observer.observe(el);
		return () => observer.disconnect();
	}, [updateScrollIndicators]);

	const prefersReducedMotion =
		typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	const scrollThumbs = React.useCallback(
		(direction: 'left' | 'right') => {
			const el = thumbsRef.current;
			if (!el) return;
			el.scrollBy({
				left: direction === 'left' ? -200 : 200,
				behavior: prefersReducedMotion ? 'auto' : 'smooth'
			});
		},
		[prefersReducedMotion]
	);

	return (
		<Box
			sx={{
				display: 'flex',
				gap: { md: 8, lg: 20 },
				width: '100%',
				maxWidth: 1120,
				height: '100%',
				maxHeight: 720,
				bgcolor: 'common.white',
				borderRadius: '12px',
				overflow: 'hidden',
				pt: { md: 8, lg: 20 },
				pl: { md: 8, lg: 20 },
				pb: { md: 8, lg: 20 },
				pr: { md: 8, lg: 12 }
			}}
		>
			{/* Left — Main image or video */}
			<Box
				sx={{
					flex: '1 1 0',
					position: 'relative',
					bgcolor: 'common.white',
					borderRadius: '8px',
					overflow: 'hidden',
					minWidth: 0,
					flexShrink: 0
				}}
			>
				{current && (
					<Box
						key={current.videoUrl ?? current.src}
						sx={{
							position: 'absolute',
							inset: 0,
							width: '100%',
							height: '100%'
						}}
					>
						{current.videoUrl ? (
							(() => {
								const ytId = getYouTubeId(current.videoUrl);
								return ytId ? (
									<Box
										component="iframe"
										src={`https://www.youtube.com/embed/${ytId}?rel=0`}
										title={current.alt ?? 'Video'}
										allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
										allowFullScreen
										sx={{
											position: 'absolute',
											inset: 0,
											width: '100%',
											height: '100%',
											border: 'none',
											borderRadius: '8px'
										}}
									/>
								) : (
									<video
										src={current.videoUrl}
										controls
										playsInline
										style={{
											position: 'absolute',
											inset: 0,
											width: '100%',
											height: '100%',
											objectFit: 'contain',
											borderRadius: '8px'
										}}
									>
										<track kind="captions" srcLang="de" label="Deutsch" />
									</video>
								);
							})()
						) : (
							<StrapiImageRenderer
								src={current.src}
								imageData={item.imageData}
								host={item.host}
								fill
								sizes="480px"
								alt={current.alt ?? ''}
								imageFit="contain"
							/>
						)}
					</Box>
				)}
			</Box>

			{/* Right — scrollable text content + thumbnail gallery */}
			<Box
				sx={{
					flex: '1 1 0',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					minWidth: 0
				}}
			>
				{/* Scrollable content */}
				<Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, pr: 2 }}>
					<Typography variant="nav" component="h2" sx={{ color: 'common.black', fontWeight: 400, mb: 4 }}>
						{item.name}
					</Typography>

					<Divider sx={{ mb: 4 }} />

					<Typography
						variant="body-sm"
						component="p"
						sx={{
							color: 'text.secondary',
							...BODY_SM_DETAIL_SX,
							mb: item.detail?.fullDescription ? 4 : 8
						}}
					>
						{rankingLabel ?? item.description}
					</Typography>

					{item.detail?.fullDescription && (
						<ComponentSharedTextEditor
							blockContent={parsedContent}
							variantBody={nullToUndefined(block?.variantBody)}
							variantHeading1={block?.variantHeading1}
							variantHeading2={block?.variantHeading2}
							variantHeading3={block?.variantHeading3}
							variantHeading4={block?.variantHeading4}
							variantHeading5={block?.variantHeading5}
							variantHeading6={block?.variantHeading6}
							overrides={{
								paragraph: {
									fontSize: '0.875rem',
									lineHeight: '1.375rem',
									letterSpacing: '0.05px',
									color: 'text.secondary',
									'@media (min-width: 768px)': {
										fontSize: '0.875rem'
									}
								},
								listItem: {
									fontSize: '0.875rem',
									'@media (min-width: 768px)': {
										fontSize: '0.875rem'
									}
								},
								heading: {
									mb: 1
								}
							}}
						/>
					)}

					{/* Meta fields */}
					<Box
						component="dl"
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 6,
							m: 0
						}}
					>
						{item.detail?.trainingCompany && (
							<MetaRow label="Ausbildungsbetrieb:" value={item.detail.trainingCompany} />
						)}
						{item.detail?.guild && <MetaRow label="Tischler-Innung:" value={item.detail.guild} />}
						{item.detail?.material && <MetaRow label="Material:" value={item.detail.material} />}
					</Box>
				</Box>

				{/* Thumbnail gallery */}
				{allImages.length > 1 && (
					<Box
						role="toolbar"
						aria-label={`Bildergalerie — ${allImages.length} Bilder`}
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 2,
							mt: 6,
							flexShrink: 0
						}}
					>
						{/* Left arrow */}
						<Box
							component="button"
							type="button"
							aria-label="Vorherige Bilder"
							aria-hidden={!canScrollLeft}
							tabIndex={canScrollLeft ? 0 : -1}
							onClick={() => scrollThumbs('left')}
							sx={{
								flexShrink: 0,
								width: 28,
								height: 28,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								border: '1px solid',
								borderColor: 'divider',
								borderRadius: '50%',
								bgcolor: 'common.white',
								cursor: 'pointer',
								p: 0,
								opacity: canScrollLeft ? 1 : 0,
								pointerEvents: canScrollLeft ? 'auto' : 'none',
								transition: 'opacity 0.2s ease, border-color 0.15s ease',
								'@media (prefers-reduced-motion: reduce)': {
									transition: 'none'
								},
								'&:hover': { borderColor: 'text.secondary' },
								'&:focusVisible': {
									outline: '2px solid',
									outlineColor: 'primary.main',
									outlineOffset: 2
								}
							}}
						>
							<svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
								<path
									d="M10 3L5.5 8L10 13"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</Box>

						{/* Scrollable thumbnails */}
						<Box
							ref={thumbsRef}
							role="group"
							aria-label="Bildergalerie"
							onScroll={updateScrollIndicators}
							sx={{
								display: 'flex',
								gap: 4,
								flex: 1,
								minWidth: 0,
								overflowX: 'auto',
								scrollbarWidth: 'none',
								'&::-webkit-scrollbar': { display: 'none' }
							}}
						>
							{allImages.map((img, i) => {
								const isVideo = isVideoEntry(img);
								const thumbSrc = isVideo && img.videoUrl ? (getYouTubeThumbnail(img.videoUrl) ?? img.src) : img.src;

								return (
									<Box
										key={img.videoUrl ?? img.src}
										component="button"
										type="button"
										onClick={() => onSelectImage(i)}
										aria-label={isVideo ? `Video ${i + 1} abspielen` : `Bild ${i + 1} anzeigen`}
										aria-current={i === selectedImageIndex ? 'true' : undefined}
										sx={{
											position: 'relative',
											width: 80,
											height: 80,
											borderRadius: '4px',
											overflow: 'hidden',
											border: '2px solid',
											borderColor: i === selectedImageIndex ? 'primary.main' : 'transparent',
											cursor: 'pointer',
											p: 0,
											bgcolor: 'transparent',
											flexShrink: 0,
											transition: 'border-color 0.15s ease',
											'@media (prefers-reduced-motion: reduce)': {
												transition: 'none'
											},
											'&:hover': {
												borderColor: i === selectedImageIndex ? 'primary.main' : 'divider'
											},
											'&:focusVisible': {
												outline: '2px solid',
												outlineColor: 'primary.main',
												outlineOffset: 2
											}
										}}
									>
										{isVideo && thumbSrc !== img.src ? (
											<Box
												component="img"
												src={thumbSrc}
												alt={img.alt ?? ''}
												sx={{
													width: '100%',
													height: '100%',
													objectFit: 'cover'
												}}
											/>
										) : (
											<StrapiImageRenderer
												src={thumbSrc}
												host={item.host}
												fill
												sizes="80px"
												alt={img.alt ?? ''}
												imageFit="cover"
											/>
										)}

										{isVideo && (
											<Box
												sx={{
													position: 'absolute',
													inset: 0,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													bgcolor: 'rgba(0,0,0,0.35)'
												}}
											>
												<PlayArrowRounded
													sx={{
														color: 'common.white',
														fontSize: 28
													}}
												/>
											</Box>
										)}
									</Box>
								);
							})}
						</Box>

						{/* Right arrow */}
						<Box
							component="button"
							type="button"
							aria-label="Weitere Bilder"
							aria-hidden={!canScrollRight}
							tabIndex={canScrollRight ? 0 : -1}
							onClick={() => scrollThumbs('right')}
							sx={{
								flexShrink: 0,
								width: 28,
								height: 28,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								border: '1px solid',
								borderColor: 'divider',
								borderRadius: '50%',
								bgcolor: 'common.white',
								cursor: 'pointer',
								p: 0,
								opacity: canScrollRight ? 1 : 0,
								pointerEvents: canScrollRight ? 'auto' : 'none',
								transition: 'opacity 0.2s ease, border-color 0.15s ease',
								'@media (prefers-reduced-motion: reduce)': {
									transition: 'none'
								},
								'&:hover': { borderColor: 'text.secondary' },
								'&:focusVisible': {
									outline: '2px solid',
									outlineColor: 'primary.main',
									outlineOffset: 2
								}
							}}
						>
							<svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
								<path
									d="M6 3L10.5 8L6 13"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</Box>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default CompetitionDetailDesktop;
